import React, { useState, createContext, useEffect, useCallback } from 'react';
import axios from 'axios';

import { defaultProspectModel, defaultTeamModel } from "./Models";
import dayjs from 'dayjs';
import { fetchPropertyByAddress } from '../services/corelogic';
import { fetchPhonesByAddress, parseAddressForSkipTrace } from '../services/skipTracing';
import { batchDataService } from '../services/batchData';
import sessionManager from '../services/sessionManager';
import { createContextAwareApiClient } from '../services/apiClient';
// trigger deployment

export const AppCTX = createContext();

export default function AppContext({ children }) {
  // Session state
  const [sessionState, setSessionState] = useState({
    isInitialized: false,
    isAuthenticated: false,
    isLoading: true,
    error: null
  });

  // User state
  const [user, setUser] = useState({
    uid: null,
    data: null,
  });

  // App state
  const [prospects, setProspects] = useState([])
  const [openAddProspect, setOpenAddProspect] = React.useState(false);
  const [getPropertyDetails, setGetPropertyDetails] = useState(false)
  const [appointments, setAppointments] = useState([])
  const [leadType, setLeadType] = useState(0)
  const [status, setStatus] = useState({ notificationType: 'neutral', message: '' })
  const [map, setMap] = useState(null);
  const [newProspect, setNewProspect] = useState(defaultProspectModel)
  const [teamData, setTeamData] = useState(defaultTeamModel)
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [propertyInsights, setPropertyInsights] = useState(null);
  const [propertyInsightsLoading, setPropertyInsightsLoading] = useState(false);
  const [propertyInsightsError, setPropertyInsightsError] = useState(null);

  // Skip Tracing state
  const [skipTracingLoading, setSkipTracingLoading] = useState(false);
  const [skipTracingStatus, setSkipTracingStatus] = useState(null); // 'loading' | 'found' | 'not_found' | 'error' | null

  // BatchData (Premium Data) state
  const [premiumData, setPremiumData] = useState(null);
  const [premiumDataLoading, setPremiumDataLoading] = useState(false);
  const [premiumDataError, setPremiumDataError] = useState(null);



  useEffect(() => {
    console.log('map loaded', isMapLoaded)
  }, [isMapLoaded])

  // Session management methods (needed for contextApiClient)
  const getAuthHeaders = async () => {
    try {
      return await sessionManager.getAuthHeaders();
    } catch (error) {
      console.error('Get auth headers error:', error);
      return {};
    }
  };

  const logout = async () => {
    try {
      await sessionManager.logout();
      setUser({ uid: null, data: null });
      setProspects([]);
      setAppointments([]);
      setTeamData(defaultTeamModel);

      setSessionState({
        isInitialized: true,
        isAuthenticated: false,
        isLoading: false,
        error: null
      });

      console.log('User logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Create context-aware API client instance (moved up)
  const authMethods = {
    getAuthHeaders,
    logout
  };

  const contextApiClient = React.useMemo(() => {
    return createContextAwareApiClient(authMethods);
  }, [authMethods]);

  // Context-aware API client hook
  const useApiClient = () => {
    return contextApiClient;
  };

  // Fetch premium data from BatchData API (Moved here to access contextApiClient)
  const fetchPremiumData = async (addressInput) => {
    setPremiumDataLoading(true);
    setPremiumDataError(null);
    try {
      // Determine format
      let addressString = '';
      let addressObj = {};

      if (typeof addressInput === 'object') {
        addressString = `${addressInput.address || addressInput.street}, ${addressInput.city}, ${addressInput.state} ${addressInput.zip}`;
        addressObj = addressInput;
      } else {
        addressString = addressInput;
        // Try to parse string or just pass it as is (BatchData backend handles string too, but object is safer)
        addressObj = addressInput;
      }

      const [propertyDetails, skipTraceResults] = await Promise.all([
        // COST SAVING: Temporarily disabled V1 Property Search to avoid charges during testing.
        // batchDataService.getPropertyDetails(addressString).catch(err => {
        //   console.warn('BatchData property lookup failed:', err.message);
        //   return null;
        // }),
        Promise.resolve(null),

        batchDataService.skipTrace(addressObj, contextApiClient)
      ]);

      setPremiumData({
        details: propertyDetails,
        contacts: skipTraceResults?.results || []
      });
    } catch (error) {
      console.error('Error fetching premium data:', error);
      setPremiumDataError('Failed to fetch premium data.');
    } finally {
      setPremiumDataLoading(false);
    }
  };

  //  When user joins this app for the first time
  // Define functions after contextApiClient is available
  const getProspects = useCallback(async () => {
    try {
      console.log('Pulling prospects from API...');

      // Make authenticated API call to get prospects
      const response = await contextApiClient.get('/api/prospects');

      if (response.success && response.prospects) {
        console.log('Prospects pulled from API:', response.prospects);

        // Normalize notes to strings for rendering
        const normalizedProspects = response.prospects.map((p) => ({
          ...p,
          notes: Array.isArray(p.notes)
            ? p.notes.map((n) => (typeof n === 'string' ? n : n.note || ''))
            : [],
        }));

        // Filter: admins see all, others see only their own prospects
        const currentUserId = user?.uid || user?.data?.id || null;
        const isAdmin = user?.data?.role === 'masteradmin' || user?.data?.role === 'admin';
        const filteredProspects = normalizedProspects.filter((p) => {
          if (isAdmin) return true;
          if (!p.setterId || !currentUserId) return false;
          return p.setterId === currentUserId;
        });

        // Separate prospects with appointments
        const appointments = [];
        const regularProspects = [];

        filteredProspects.forEach((prospect) => {
          if (prospect.appointmentStart !== null && prospect.appointmentStart !== undefined) {
            appointments.push(prospect);
          } else {
            regularProspects.push(prospect);
          }
        });

        console.log('Appointments found:', appointments);
        console.log('Regular prospects:', regularProspects);

        setAppointments(appointments);
        setProspects(regularProspects);
      } else {
        console.error('API response error:', response);
        setStatus({ notificationType: 'error', message: 'Failed to load prospects' });
      }
    } catch (error) {
      console.error('Error fetching prospects from API:', error);
      setStatus({ notificationType: 'error', message: 'Error loading prospects' });
      return [];
    }
  }, [contextApiClient, setAppointments, setProspects, setStatus, user?.uid, user?.data?.role])

  const getAppointments = useCallback(async () => {
    try {
      console.log('Pulling appointments from API...');

      // Make authenticated API call to get prospects (which includes appointments)
      const response = await contextApiClient.get('/api/prospects');

      if (response.success && response.prospects) {
        // Filter only prospects with appointments
        const appointments = response.prospects.filter(prospect =>
          prospect.appointmentStart !== null && prospect.appointmentStart !== undefined
        );

        console.log('Appointments found:', appointments);
        setAppointments(appointments);
      } else {
        console.error('API response error:', response);
        setStatus({ notificationType: 'error', message: 'Failed to load appointments' });
      }
    } catch (error) {
      console.error('Error fetching appointments from API:', error);
      setStatus({ notificationType: 'error', message: 'Error loading appointments' });
      return [];
    }
  }, [contextApiClient, setAppointments, setStatus])

  // Initialize session manager and load existing session
  useEffect(() => {
    const initializeSession = async () => {
      try {
        setSessionState(prev => ({ ...prev, isLoading: true, error: null }));

        // Initialize session manager
        await sessionManager.initialize();

        // Check if we have a valid session
        const isAuthenticated = await sessionManager.isAuthenticated();
        const isSessionValid = await sessionManager.isSessionValid();

        if (isAuthenticated && isSessionValid) {
          // Load user data from session
          const userData = await sessionManager.getUserData();
          const accessToken = await sessionManager.getAccessToken();

          if (userData && accessToken) {
            // Extract uid from userData or accessToken
            const uid = userData.id || userData.uid;

            setUser({
              uid: uid,
              data: userData
            });

            setSessionState({
              isInitialized: true,
              isAuthenticated: true,
              isLoading: false,
              error: null
            });

            console.log('Session restored successfully:', { uid, userData });
          } else {
            // Invalid session data
            await sessionManager.clearSession();
            setSessionState({
              isInitialized: true,
              isAuthenticated: false,
              isLoading: false,
              error: null
            });
          }
        } else {
          // No valid session
          setSessionState({
            isInitialized: true,
            isAuthenticated: false,
            isLoading: false,
            error: null
          });
        }
      } catch (error) {
        console.error('Failed to initialize session:', error);
        setSessionState({
          isInitialized: true,
          isAuthenticated: false,
          isLoading: false,
          error: error.message
        });
      }
    };

    initializeSession();
  }, []);

  useEffect(() => {
    console.log('User state changed:', user);
    // Check if user is authenticated and has data
    if (user.uid !== null && user.data != null) {
      console.log('USER FOUND - Full authentication complete', user)
      getProspects()
      getTeamData()
      // getAppointments() // Disabled: Feature not fully implemented, causes error toast on load
    } else if (user.uid !== null && user.data == null) {
      console.log('USER UID SET - Waiting for data (invite consumption)', user)
    } else {
      console.log('USER NOT AUTHENTICATED', user)
    }
  }, [user])

  useEffect(() => {
    if (leadType > 0) {
      setNewProspect(defaultProspectModel)
    }
  }, [leadType])

  // Removed unused getPreciselyToken function


  const addProspect = async (newProspect) => {
    try {
      console.log('Creating prospect via API:', newProspect);

      // Transform prospect data to match API format
      const prospectData = {
        address: newProspect.address,
        coords: newProspect.coords,
        owners: newProspect.owners || [],
        systemInstalled: newProspect.systemInstalled || false,
        markerStatus: newProspect.markerStatus || 'default',
        appointmentStart: newProspect.appointmentStart || null,
        appointmentSet: newProspect.appointmentSet || false,
        notes: newProspect.notes || [],
        utilityBill: newProspect.utilityBill || null
      };

      const response = await contextApiClient.post('/api/prospects', prospectData);

      if (response.success) {
        setStatus({ notificationType: 'success', message: 'Prospect added successfully' });

        // Clear the coordinates after successful addition to remove pin and buttons
        setNewProspect(prev => ({
          ...prev,
          coords: null
        }));

        // Refresh prospects list
        getProspects();
      } else {
        setStatus({ notificationType: 'error', message: response.error || 'Failed to add prospect' });
      }

    } catch (err) {
      console.error('Error adding prospect:', err);
      setStatus({ notificationType: 'error', message: 'Something went wrong' });
    }
  }

  const updateProspect = async (prospectToUpdate) => {
    try {
      console.log('Updating prospect via API:', prospectToUpdate);

      // Transform prospect data to match API format
      const prospectData = {
        address: prospectToUpdate.address,
        coords: prospectToUpdate.coords,
        owners: prospectToUpdate.owners || [],
        systemInstalled: prospectToUpdate.systemInstalled || false,
        markerStatus: prospectToUpdate.markerStatus || 'default',
        appointmentStart: prospectToUpdate.appointmentStart || null,
        appointmentSet: prospectToUpdate.appointmentSet || false,
        notes: prospectToUpdate.notes || [],
        utilityBill: prospectToUpdate.utilityBill || null
      };

      // Use PUT request to update prospect (assuming the API supports this)
      const response = await contextApiClient.put(`/api/prospects/${prospectToUpdate.id}`, prospectData);

      if (response.success) {
        setStatus({ notificationType: 'success', message: 'Client updated successfully' });
        // Refresh prospects list
        getProspects();
      } else {
        setStatus({ notificationType: 'error', message: response.error || 'Failed to update prospect' });
      }
    } catch (err) {
      console.error('Error updating prospect:', err);
      setStatus({ notificationType: 'error', message: 'Something went wrong' });
    }
  }

  const scheduleAppointment = async (prospect, day, time, selectedCloser, setNewAppointment) => {
    let dateTime = dayjs(day).format('YYYY-MM-DD') + "T" + dayjs(time).format('HH:mm')
    try {
      console.log('Scheduling appointment via API:', { prospect, dateTime, selectedCloser });

      // Update the prospect with appointment details
      const updateData = {
        appointmentStart: dateTime,
        closer: selectedCloser,
        appointmentSet: true,
      };

      const response = await contextApiClient.put(`/api/prospects/${prospect.id}`, updateData);

      if (response.success) {
        setStatus({ notificationType: 'success', message: 'Appointment added' });
        // Refresh prospects list to show updated data
        getProspects();
      } else {
        setStatus({ notificationType: 'error', message: response.error || 'Failed to schedule appointment' });
      }
    } catch (error) {
      console.error('Error scheduling appointment:', error);
      setStatus({ notificationType: 'error', message: 'Failed to schedule appointment' });
    }
  }

  const deleteProspect = async (prospectId) => {
    try {
      console.log('Deleting prospect via API:', prospectId);

      const response = await contextApiClient.delete(`/api/prospects/${prospectId}`);

      if (response.success) {
        setStatus({ notificationType: 'success', message: 'Prospect deleted successfully' });
        // Refresh prospects list
        getProspects();
      } else {
        setStatus({ notificationType: 'error', message: response.error || 'Failed to delete prospect' });
      }
    } catch (err) {
      console.error('Error deleting prospect:', err);
      setStatus({ notificationType: 'error', message: 'Something went wrong' });
    }
  }

  const sendSmsReminders = async (hopBackTime) => {
    const url = `http://localhost:5001/door-hopper/us-central1/helloWorld`;
    console.log('sending message')
    try {
      await axios.post(url, { address: newProspect.address, time: hopBackTime });
      setStatus({ notificationType: 'success', message: 'SMS notifcations setup successfully' })
    } catch (err) {
      setStatus({ notificationType: 'error', message: 'Something went wrong' })
    }
  }

  const getTeamData = async () => {
    try {
      const result = await contextApiClient.get('/api/teamdata');
      console.log('retrieved team data', result);

      // Transform API response to match expected format
      const transformedData = {
        ...result,
        members: result.teamMembers || [] // Map teamMembers to members
      };

      setTeamData(transformedData);
    } catch (err) {
      console.log(err);
    }
  }



  const addAddress = useCallback(async () => {
    try {
      if (!newProspect?.coords?.lat || !process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
        setStatus({ notificationType: 'error', message: 'Missing coordinates or API key' });
        return;
      }
      const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${newProspect.coords.lat},${newProspect.coords.lng}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;
      const { data } = await axios.get(url);
      console.log('response data ;', data)
      if (!data?.results?.[0]?.formatted_address) {
        setStatus({ notificationType: 'error', message: 'API ERROR' });
        return;
      } else {
        const temp = { ...newProspect, address: data?.results?.[0]?.formatted_address }
        console.log('address added', temp)
        setNewProspect(temp)
      }
    } catch (error) {
      console.error('Address error:', error);
      setStatus({
        notificationType: 'error',
        message: error.response?.data?.error_message || 'API ERROR'
      });
    }
  }, [newProspect, setStatus, setNewProspect]);

  // Fetch CoreLogic property insights by address and enrich newProspect
  const fetchPropertyInsights = async (addressObj) => {
    setPropertyInsightsLoading(true);
    setPropertyInsightsError(null);
    setPropertyInsights(null);
    setSkipTracingLoading(true);
    setSkipTracingStatus('loading');

    // Reset Premium Data State when switching/fetching new property
    setPremiumData(null);
    setPremiumDataError(null);
    setPremiumDataLoading(false);

    try {
      // Parse zip code from address if not provided
      let zipCode = addressObj.zipCode;
      console.log('[CoreLogic] Initial zip code from addressObj:', zipCode);

      if (!zipCode && addressObj.streetAddress) {
        // Extract zip code from the end of the address (e.g., "123 Main St, City, TX 75009")
        const zipMatch = addressObj.streetAddress.match(/\b\d{5}\b/);
        if (zipMatch) {
          zipCode = zipMatch[0];
          console.log('[CoreLogic] Extracted zip code from addressObj.streetAddress:', zipCode);
        }
      }

      // If we still don't have a zip code, try to extract from newProspect.address
      if (!zipCode && newProspect?.address) {
        const zipMatch = newProspect.address.match(/\b\d{5}\b/);
        if (zipMatch) {
          zipCode = zipMatch[0];
          console.log('[CoreLogic] Extracted zip code from newProspect.address:', zipCode);
        }
      }

      if (!zipCode) {
        console.error('[CoreLogic] No zip code found in:', {
          addressObj,
          newProspectAddress: newProspect?.address
        });
        throw new Error('Zip code is required for property search. Please ensure the address includes a zip code.');
      }

      // Prepare address object with zip code
      const addressWithZip = {
        streetAddress: addressObj.streetAddress,
        city: addressObj.city || '',
        state: addressObj.state || '',
        zipCode: zipCode
      };

      console.log('[CoreLogic] Fetching property insights with:', addressWithZip);

      console.log('[CoreLogic] Fetching property insights with:', addressWithZip);

      // SEQUENTIAL FETCH: CoreLogic First -> Then Skip Trace with Owner Name

      // 1. Fetch CoreLogic Data
      let propertyData = null;
      try {
        propertyData = await fetchPropertyByAddress(addressWithZip);
        console.log('[CoreLogic] Property Insights:', propertyData);
      } catch (err) {
        console.error('[CoreLogic] Failed:', err);
      }

      // 2. Prepare Data for UI
      // deep clone to ensure React state triggers
      const data = propertyData ? JSON.parse(JSON.stringify(propertyData)) : null;

      // NOTE: Skip Tracing is now handled exclusively via the "Get Premium Data" button (BatchData).
      // We no longer automatically skip trace on property load to save credits.
      setSkipTracingLoading(false);
      setSkipTracingStatus(null);

      setPropertyInsights(data);

      // Enrich newProspect with CoreLogic data
      if (data) {
        const enrichedProspect = { ...newProspect };

        // Extract homeowner information
        const ownership = data.ownership;
        if (ownership?.currentOwners) {
          const owners = ownership.currentOwners.ownerNames || [];

          // Filter out null/empty owners and only include valid ones
          const validOwners = owners.filter(owner =>
            owner &&
            owner.fullName && // Must have a fullName
            owner.fullName.trim() !== '' // Must not be empty string
          );

          console.log('[CoreLogic] Owner analysis:', {
            total: owners.length,
            valid: validOwners.length,
            nullCount: owners.length - validOwners.length,
            validOwners: validOwners.map(o => o.fullName)
          });

          // Convert to prospect owner format (for AddProspect form)
          // Start with empty phone numbers - will be filled by BatchData Premium Skip Trace on-demand
          enrichedProspect.owners = validOwners.map(owner => ({
            name: owner.fullName.trim(),
            number: '', // Phone numbers fetched via BatchData Premium Skip Trace on-demand
            isCorporate: owner.isCorporate || false
          }));

          // NOTE: RapidAPI skip tracing has been DISABLED in favor of BatchData Premium.
          // Phone numbers are now fetched on-demand when user clicks "Fetch Premium Data" button.
          // This saves API credits by not auto-fetching for every property lookup.


          // Determine initial marker status based on mailing address comparison
          const mailingInfo = ownership.currentOwnerMailingInfo;

          // If mailing address differs, treat as prospect; statuses are locked to defined set
          enrichedProspect.markerStatus = 'default';

          // Add property details
          enrichedProspect.propertyDetails = {
            clipId: data.clip,
            propertyAddress: data.propertyAddress,
            ownership: ownership
          };

          console.log('[CoreLogic] Enriched prospect with:', {
            owners: enrichedProspect.owners,
            markerStatus: enrichedProspect.markerStatus,
            propertyDetails: enrichedProspect.propertyDetails
          });

          setNewProspect(enrichedProspect);
        }
      }
    } catch (err) {
      setPropertyInsightsError(err.message || 'Failed to fetch property insights');
      setPropertyInsights(null);
      console.error('[CoreLogic] Error:', err);
    } finally {
      setPropertyInsightsLoading(false);
    }
  };

  // Auto-fetch address whenever newProspect has coords but no address
  useEffect(() => {
    if (newProspect?.coords && !newProspect?.address) {
      console.log('[Address] Auto-fetching address for new coordinates:', newProspect.coords);
      addAddress();
    }
  }, [newProspect?.coords?.lat, newProspect?.coords?.lng, newProspect?.address, addAddress]);

  // Clear property insights and address when coordinates change (new location selected)
  useEffect(() => {
    if (newProspect?.coords) {
      console.log('[CoreLogic] Coordinates changed, clearing previous property insights and address');
      setPropertyInsights(null);
      setPropertyInsightsError(null);
      setPropertyInsightsLoading(false);

      // Clear the old address to prevent using stale data
      setNewProspect(prev => ({
        ...prev,
        address: '', // Clear old address when coordinates change
        owners: [], // Clear old owner data
        markerStatus: 'default', // Reset marker status
        propertyDetails: null // Clear property details
      }));
    }
  }, [newProspect?.coords?.lat, newProspect?.coords?.lng, newProspect?.coords]);

  // Function to manually clear property insights
  const clearPropertyInsights = () => {
    setPropertyInsights(null);
    setPropertyInsightsError(null);
    setPropertyInsightsLoading(false);
  };

  // Session management methods
  const login = async (accessToken, refreshToken, userData, expiresIn) => {
    try {
      setSessionState(prev => ({ ...prev, isLoading: true, error: null }));

      const success = await sessionManager.setSession(accessToken, refreshToken, userData, expiresIn);

      if (success) {
        const uid = userData.id || userData.uid;
        setUser({
          uid: uid,
          data: userData
        });

        setSessionState({
          isInitialized: true,
          isAuthenticated: true,
          isLoading: false,
          error: null
        });

        console.log('Login successful:', { uid, userData });
        return true;
      } else {
        setSessionState(prev => ({
          ...prev,
          isLoading: false,
          error: 'Failed to store session'
        }));
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      setSessionState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message
      }));
      return false;
    }
  };

  const refreshSession = async () => {
    try {
      const isValid = await sessionManager.isSessionValid();
      if (isValid) {
        const userData = await sessionManager.getUserData();
        const uid = userData?.id || userData?.uid;

        if (userData && uid) {
          setUser({
            uid: uid,
            data: userData
          });

          setSessionState(prev => ({
            ...prev,
            isAuthenticated: true,
            error: null
          }));

          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Session refresh error:', error);
      return false;
    }
  };

  // Update territory for a specific team member
  const updateTerritory = useCallback(async (name, coordinates) => {
    try {
      // 1. Update local state first (optimistic update)
      const updatedTeamData = {
        ...teamData,
        areas: {
          ...teamData.areas,
          [name]: coordinates
        }
      };
      setTeamData(updatedTeamData);

      // 2. Call backend API to persist the territory
      await contextApiClient.put(`/api/updateUser/territory/${encodeURIComponent(name)}`, { coordinates });

      // 3. Show success message
      setStatus({ notificationType: 'success', message: 'Territory updated successfully' });

    } catch (error) {
      console.error('Error updating territory:', error);

      // 4. Revert local state on error
      setTeamData(teamData);

      // 5. Show error message
      setStatus({
        notificationType: 'error',
        message: error.message || 'Failed to update territory'
      });
    }
  }, [teamData, setTeamData, contextApiClient, setStatus]);

  // Update area manager flag for a specific team member
  const updateAreaManager = useCallback(async (name, areaManager) => {
    try {
      // 1. Optimistic local update across both teamMembers and members (some components read either)
      const updatedTeamMembers = (teamData?.teamMembers || []).map(member =>
        member.name === name ? { ...member, areaManager } : member
      );
      const updatedMembers = (teamData?.members || []).map(member =>
        member.name === name ? { ...member, areaManager } : member
      );

      const previousTeamData = teamData;
      const optimistic = { ...teamData, teamMembers: updatedTeamMembers, members: updatedMembers };
      setTeamData(optimistic);

      // 2. Persist to backend API
      await contextApiClient.put(`/api/updateUser/areamanager/${encodeURIComponent(name)}`, { areaManager });

      // 3. Notify success
      setStatus({ notificationType: 'success', message: 'Area manager status updated' });
    } catch (error) {
      console.error('Error updating area manager:', error);
      // Revert on error
      setTeamData(teamData);
      setStatus({ notificationType: 'error', message: error.message || 'Failed to update area manager' });
    }
  }, [teamData, setTeamData, contextApiClient, setStatus]);

  return (
    <AppCTX.Provider value={{
      // Session state and methods
      sessionState,
      login,
      logout,
      refreshSession,
      getAuthHeaders,
      useApiClient,

      // App state
      isMapLoaded,
      setIsMapLoaded,
      setMap,
      map,
      status,
      setStatus,
      sendSmsReminders,

      // Prospects
      prospects,
      setProspects,
      getProspects,
      newProspect,
      setNewProspect,
      addProspect,
      updateProspect,
      deleteProspect,

      // Lead management
      leadType,
      setLeadType,

      // Appointments
      appointments,
      setAppointments,
      scheduleAppointment,

      // User management
      user,
      setUser,

      // Team data
      teamData,
      setTeamData,
      updateTerritory,
      updateAreaManager,

      // UI state
      openAddProspect,
      setOpenAddProspect,
      addAddress,

      // Property insights
      getPropertyDetails,
      setGetPropertyDetails,
      propertyInsights,
      setPropertyInsights,
      propertyInsightsLoading,
      propertyInsightsError,
      fetchPropertyInsights,
      clearPropertyInsights,

      // Skip Tracing
      skipTracingLoading,
      skipTracingStatus,

      // BatchData (Premium Data)
      premiumData,
      premiumDataLoading,
      premiumDataError,
      fetchPremiumData,
    }}>
      {children}
    </AppCTX.Provider>
  )
}
